/**
 * A Perf V2 strategist review → Recommendations cards.
 *
 * The review is one account-wide reading (campaign → market → account) that names
 * its recommendations against platform campaign ids, market codes or "account".
 * Each becomes one card in the shape RecCard already renders, so it sorts, filters,
 * dismisses and tiers beside every other card. Where a review is served it is the
 * ONLY source of Recommendations for the campaigns it read: usePerformanceRuns drops
 * the V1 performance cards for those campaigns.
 *
 * What the card does not carry: an Apply button. The review states intent (lever,
 * direction, a size from → to), not an executable edit, so every card is advisory
 * and says so. Whether the change was made is read from the account's change history
 * by the next review, not from a button here.
 */
import { asText } from './performanceCards';
import { plainText } from '../components/OptimizationWorkspace/reviewText';

export const STRATEGIST_AGENT = 'Strategist';

const RATING = { high: 3, medium: 2, low: 1 };
const LEVELS = ['account', 'market', 'campaign'];
const LEVEL_LABEL = { account: 'Account', market: 'Market', campaign: 'Campaign' };
const METRIC_LABEL = { objective_per_day: 'objective per day', cost_per_objective: 'cost per objective' };
const UNIT_SUFFIX = { 'daily budget': '/day', 'target CPA': ' target CPA', 'target ROAS': ' target ROAS' };

const ADVISORY = 'Strategist recommendations are advisory: make the change in Google Ads. '
  + 'The next review reads the change history and records whether it was made.';
const DECISION_ADVISORY = 'A decision for the team: no single change follows until it is made.';

/** The dossier's fact ids ("[INV.read.90]", "[K.10]") are for the checker, not the reader. */
export function stripFacts(value) {
  const text = asText(value);
  return text ? plainText(text, {}) || null : null;
}

/** First sentence, for the collapsed row. Never cut mid-word. */
function firstSentence(value) {
  const text = stripFacts(value);
  if (!text) return null;
  const match = text.match(/^.+?[.;](?=\s|$)/);
  return (match ? match[0] : text).replace(/;$/, '.');
}

/** Grouped by hand: the page renders the same digits in every runtime locale. */
function grouped(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return asText(value);
  const [whole, fraction] = String(Math.round(n * 100) / 100).split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''}`;
}

const amount = (value, unit) => `${grouped(value)}${UNIT_SUFFIX[unit] ?? (unit ? ` ${unit}` : '')}`;

const isCampaignId = (entity) => /^\d{6,}$/.test(String(entity || ''));

/** The size as the card's Current → Recommended pair; a reallocation names each campaign. */
function sizePair(size, nameOf) {
  if (Array.isArray(size)) {
    const moves = size.filter((m) => m && m.from != null && m.to != null);
    if (!moves.length) return { cur: null, rec: null };
    return {
      cur: moves.map((m) => `${nameOf(m.campaign_id)}: ${amount(m.from, m.unit)}`).join('; '),
      rec: moves.map((m) => `${nameOf(m.campaign_id)}: ${amount(m.to, m.unit)}`).join('; '),
    };
  }
  if (size && typeof size === 'object' && size.from != null && size.to != null) {
    // A hold sizes from == to; "3,150 → 3,150" reads like a change that was lost.
    const held = Number(size.from) === Number(size.to);
    return { cur: amount(size.from, size.unit), rec: held ? 'No change: hold' : amount(size.to, size.unit) };
  }
  return { cur: null, rec: null };
}

function driversText(drivers) {
  const rows = (Array.isArray(drivers) ? drivers : [])
    .map((d) => {
      const text = stripFacts(d?.driver);
      if (!text) return null;
      return d.strength ? `${d.strength.charAt(0).toUpperCase()}${d.strength.slice(1)}: ${text}` : text;
    })
    .filter(Boolean);
  return rows.length ? rows.join(' · ') : null;
}

function stopRuleText(rule, nameOf) {
  if (!rule || typeof rule !== 'object' || rule.value == null) return null;
  const metric = METRIC_LABEL[rule.metric] || asText(rule.metric) || 'the metric';
  const days = rule.matured_days ? ` for ${rule.matured_days} matured day${rule.matured_days === 1 ? '' : 's'}` : '';
  const where = rule.campaign_id ? `${nameOf(rule.campaign_id)}'s ` : '';
  return `Revert if ${where}${metric} goes ${rule.when === 'below' ? 'below' : 'above'} ${grouped(rule.value)}${days}.`;
}

function confidenceText(confidence) {
  if (!confidence || typeof confidence !== 'object') return null;
  const parts = [['observation', 'what was seen'], ['explanation', 'why'], ['intervention', 'that the change works']]
    .filter(([key]) => confidence[key])
    .map(([key, label]) => `${confidence[key]} on ${label}`);
  return parts.length ? parts.join(' · ') : null;
}

function importanceOf(item) {
  const importance = item?.importance && typeof item.importance === 'object' ? item.importance : {};
  const ratings = Object.fromEntries(LEVELS.map((level) => [level, String(importance[level]?.rating || '').toLowerCase()]));
  const shown = LEVELS.filter((level) => RATING[ratings[level]]);
  return {
    ratings,
    // "Material at" reads like the tiering line every other card has.
    text: shown.length ? shown.map((level) => `${LEVEL_LABEL[level]}: ${ratings[level]}`).join(' · ') : null,
    // Material at ANY level leads: what is small for the account can still matter for its campaign.
    material: shown.some((level) => RATING[ratings[level]] >= RATING.medium),
    top: shown.reduce((best, level) => (RATING[ratings[level]] > RATING[best] ? ratings[level] : best), ratings[shown[0]] || ''),
    reasons: shown.map((level) => stripFacts(importance[level]?.why)).filter(Boolean),
  };
}

/**
 * Where the review placed an item (campaign-services strategist/gates.py, served as review.placement):
 * main or behind More, and its framing. Reviews saved before placement fall back to the importance rating.
 */
const FRAMING = {
  test: ['Test: could matter a lot, evidence still thin', 'Large enough to matter, but the evidence cannot yet carry it'],
  waiting: ['Waiting for more evidence', 'Small, and the evidence cannot yet carry it'],
  watch: [null, null],
  action: [null, null],
};
function placed(review, item, fallback) {
  const row = review.placement?.[String(item?.id)];
  if (!row) return { material: fallback, label: null, reason: null };
  const [label, why] = FRAMING[row.framing] || [null, null];
  const because = (row.weak_because || []).join('; ');
  return { material: row.placement === 'main', label, reason: why ? `${why}${because ? `: ${because}` : ''}.` : null };
}

/** Account impact first, then market, then campaign; strategic before tactical on a tie. */
function rankKey(item) {
  const { ratings } = importanceOf(item);
  return [...LEVELS.map((level) => RATING[ratings[level]] || 0), item?.scale === 'strategic' ? 1 : 0];
}

function compareRank(a, b) {
  const ka = rankKey(a);
  const kb = rankKey(b);
  for (let i = 0; i < ka.length; i += 1) if (ka[i] !== kb[i]) return kb[i] - ka[i];
  return 0;
}

/** Entities → the card's campaign fields, resolving platform ids to our rows where we have them. */
function placement(entities, review, campaignFor) {
  const list = (Array.isArray(entities) ? entities : []).map((e) => String(e));
  const campaignIds = list.filter(isCampaignId);
  const nameOf = (id) => campaignFor(id)?.name || review.campaignNames?.[id] || id;
  const rows = campaignIds.map((id) => campaignFor(id)).filter(Boolean);
  const single = campaignIds.length === 1 && list.length === 1 ? campaignFor(campaignIds[0]) : null;
  return {
    nameOf,
    camp: single?.id || null,
    campName: campaignIds.length === 1 && list.length === 1 ? nameOf(campaignIds[0]) : null,
    dest: single?.destination || null,
    pcid: campaignIds.length === 1 ? campaignIds[0] : null,
    affectedCampaigns: [...new Set(rows.map((row) => row.id))],
    affectedCampaignNames: list.map((e) => (isCampaignId(e) ? nameOf(e) : e === 'account' ? 'Whole account' : `Market ${e}`)),
  };
}

function baseCard(review, item, kind, where, rank) {
  const key = item.issue_key || `${kind}:${[...(item.entities || [])].map(String).sort().join(',')}`;
  return {
    // The review's own item id too: a fire's or fix's issue key has no level or lever, so two
    // fixes on one campaign share a key, and a shared card id merges them into one card.
    id: `strategist-${review.customerId}-${key}-${item.id || rank}`,
    index: rank,
    messageId: null,
    raw: item,
    route: 'recommendations',
    camp: where.camp,
    campName: where.campName,
    dest: where.dest,
    pcid: where.pcid,
    affectedCampaigns: where.affectedCampaigns,
    affectedCampaignNames: where.camp ? [] : where.affectedCampaignNames,
    agent: STRATEGIST_AGENT,
    cat: kind,
    // The issue key is the finding's identity across reviews (engine.py issue_key), so a
    // dismissal keyed on it (findingKey: agent|action|target type|target id) follows the
    // issue rather than this review's R1/R2 numbering.
    tid: key,
    tn: where.affectedCampaignNames.join(', ') || null,
    eids: [],
    tids: [],
    coi: null,
    sar: null,
    ccy: null,
    pri: null,
    conf: null,
    lm: null, ld: null, lp: null, ll: null, lh: null,
    auto: false,
    judge: null,
    judgeReason: null,
    suppressedReason: null,
    recur: null,
    applied: null,
    appliedEdit: null,
    origin: 'Strategist review',
    originDetail: review.dataThrough ? `data to ${review.dataThrough}` : null,
    conflict: false,
    dupe: false,
    n: 1,
    nlev: 1,
    agents: [],
    elsewhere: [],
  };
}

function recommendationCard(review, item, rank, campaignFor) {
  const where = placement(item.entities, review, campaignFor);
  const importance = importanceOf(item);
  const shown = placed(review, item, importance.material);
  const { cur, rec } = sizePair(item.size, where.nameOf);
  return {
    ...baseCard(review, item, 'recommendation', where, rank),
    action: asText(item.lever) || 'other',
    tt: asText(item.level) || null,
    head: stripFacts(item.headline),
    plain: stripFacts(item.why),
    cons: firstSentence(item.why),
    ctx: null,
    ev: driversText(item.drivers),
    me: null,
    cur,
    rec,
    priBand: importance.top || null,
    advisoryReason: ADVISORY,
    materialityPlacement: shown.label || importance.text,
    placementReason: shown.reason || importance.reasons.join(' ') || null,
    strategist: {
      kind: 'recommendation',
      material: shown.material,
      scale: asText(item.scale),
      direction: asText(item.direction),
      priority: stripFacts(item.priority),
      confidence: confidenceText(item.confidence),
      expectedEffect: stripFacts(item.expected_effect),
      tradeoff: stripFacts(item.tradeoff),
      competing: stripFacts(item.competing_explanation),
      fundedBy: stripFacts(item.funded_by),
      freesBudgetFor: stripFacts(item.frees_budget_for),
      pacing: stripFacts(item.pacing_effect),
      evaluation: stripFacts(item.evaluation_plan),
      reverseIf: stripFacts(item.reverse_if),
      stopRule: stopRuleText(item.stop_rule, where.nameOf),
      accountContext: stripFacts(item.account_context),
    },
  };
}

/** A fire (a sudden break the fire screen found) or a fix → a Diagnostics card. */
function diagnosticCard(review, item, kind, rank, campaignFor) {
  const where = placement(item.entities, review, campaignFor);
  return {
    ...baseCard(review, item, kind, where, rank),
    route: 'diagnostics',
    action: kind,
    tt: kind,
    head: stripFacts(item.issue),
    plain: null,
    cons: firstSentence(item.fix),
    ctx: null,
    ev: null,
    me: null,
    cur: null,
    rec: null,
    priBand: kind === 'fire' ? 'high' : null,
    advisoryReason: ADVISORY,
    materialityPlacement: null,
    placementReason: null,
    strategist: {
      kind,
      // A fire cites a break the code screen measured: it always leads. A fix leads unless the review
      // placed it behind More.
      material: kind === 'fire' || placed(review, item, true).material,
      fix: stripFacts(item.fix),
      whyNow: stripFacts(item.why_now),
      owner: item.owner && item.owner !== 'unknown' ? asText(item.owner) : null,
      effort: asText(item.effort),
      confidence: confidenceText(item.confidence),
    },
  };
}

function optionsText(options) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      const path = stripFacts(option?.path);
      if (!path) return null;
      const what = stripFacts(option.what_happens);
      const risks = stripFacts(option.risks);
      return `${path}${what ? `: ${what}` : ''}${risks ? ` Risk: ${risks}` : ''}`;
    })
    .filter(Boolean);
}

function decisionCard(review, item, rank, campaignFor) {
  const where = placement(item.entities, review, campaignFor);
  const options = optionsText(item.options);
  return {
    ...baseCard(review, item, 'decision', where, rank),
    action: 'decision',
    tt: 'decision',
    head: stripFacts(item.question),
    plain: null,
    cons: options.length ? `${options.length} options to choose between.` : null,
    ctx: null,
    ev: driversText(item.drivers),
    me: null,
    cur: null,
    rec: null,
    priBand: null,
    advisoryReason: DECISION_ADVISORY,
    materialityPlacement: null,
    placementReason: null,
    strategist: {
      kind: 'decision',
      // A decision is raised because the review could not settle it alone; it always leads.
      material: true,
      options,
      recommendedIf: stripFacts(item.recommended_if),
    },
  };
}

/**
 * Every served review → cards: recommendations ranked by importance (account, then market,
 * then campaign) and decisions on Recommendations; fires, then fixes, on Diagnostics.
 *
 * @param {Array<object>} reviews  /runs `strategist`: [{customerId, dataThrough, campaignNames,
 *                                 campaigns: {platformId: {id, name, destination}}, recommendations, decisions}]
 * @param {Map<string, object>} [known]  platform id → {id, name, destination} from the V1 runs,
 *                                 which resolve platform ids our campaigns row has not been stamped with.
 */
export function cardsFromStrategistReviews(reviews = [], known = new Map()) {
  const cards = [];
  for (const review of Array.isArray(reviews) ? reviews : []) {
    if (!review || typeof review !== 'object') continue;
    const campaignFor = (id) => review.campaigns?.[id] || known.get(String(id)) || null;
    const recommendations = (review.recommendations || []).filter((item) => item && typeof item === 'object' && item.headline);
    const ranked = [...recommendations].sort(compareRank);
    ranked.forEach((item) => cards.push(recommendationCard(review, item, cards.length, campaignFor)));
    (review.decisions || []).filter((item) => item && typeof item === 'object' && item.question)
      .forEach((item) => cards.push(decisionCard(review, item, cards.length, campaignFor)));
    // Fires first: they are the same-day breaks.
    [['fire', review.fires], ['fix', review.fixes]].forEach(([kind, items]) => {
      (Array.isArray(items) ? items : []).filter((item) => item && typeof item === 'object' && item.issue)
        .forEach((item) => cards.push(diagnosticCard(review, item, kind, cards.length, campaignFor)));
    });
  }
  return cards;
}

/** The platform campaign ids a set of reviews read: the V1 Recommendations they replace. */
export function strategistCoverage(reviews = []) {
  const covered = new Set();
  for (const review of Array.isArray(reviews) ? reviews : []) {
    Object.keys(review?.campaignNames || {}).forEach((id) => covered.add(String(id)));
  }
  return covered;
}
